import { sircapApi } from "shared/api/sircap";
import { EntityType } from "static/types";
import { fbTrackDepositCredits } from "utils/fbEvents";
import { createStore, useStore } from "zustand";
import { persist } from "zustand/middleware";
import { IAsset } from "entities/market";
import { getSubAccountId, MultiLangValueType } from "helpers/functions";
import {
  filterBySubAccount,
  mergeShowConfig,
  normalizeAccountsPayload,
} from "./lib/accounts";

interface ICurrency {
  id: number;
  code: string;
  flag: string | null;
  symbol: string;
}
export interface IRate {
  pair: string;
  from: string;
  to: string;
  bid: number;
  ask: number;
}
interface IUserRate {
  price_asset: string;
  trade_asset: string;
}

export interface IUserDepositAccount {
  id: number;
  user_id: number;
  user_type: EntityType;
  currency: ICurrency;
  amount: number;
  blocked_amount: number;
  sub_account: string | null;
}
export interface IUserBrokerageAccount {
  id: number;
  user_id: number;
  user_type: EntityType;
  asset: IAsset;
  quantity: number;
  blocked_quantity: number;
  date: Date;
  data?: any;
  price: number;
  pnl?: number;
  sub_account: string | null;
}

enum Currency {
  AMD = "AMD",
  USD = "USD",
  EUR = "EUR",
  RUB = "RUB",
}

const defaultUserRates: IUserRate[] = [
  { trade_asset: Currency.RUB, price_asset: Currency.AMD },
  { trade_asset: Currency.USD, price_asset: Currency.AMD },
  { trade_asset: Currency.EUR, price_asset: Currency.AMD },
];

const defaultUserAccountsShowConfig = {
  AMD: true,
};

type DashboardState = {
  prevPathDeal: boolean;
  setPrevPathDeal: any;
  currencies: ICurrency[];
  user_rates: IUserRate[];
  user_deposit_accounts: IUserDepositAccount[];
  user_brokerage_accounts: IUserBrokerageAccount[];
  user_accounts_show_config: Record<string, boolean>;
  balanceCurrency: string;
  totalAmountBottomsheetSelected: string;
  setTotalAmountBottomsheetSelected: (v: string) => void;
  performanceAmountBottomsheetSelected: string;
  setPerformanceAmountBottomsheetSelected: (v: string) => void;

  setBalanceCurrency: (v: string) => void;
  updateRates: (list: IUserRate[]) => void;
  updateAccountsShowConfig: (list: Record<string, boolean>) => void;
  applyUserAccounts: (raw: unknown) => IUserDepositAccount[];
  loadUserAccounts: () => Promise<void>;
  loadCurrencies: Function;
};

export const dashboardStore = createStore<DashboardState>()(
  persist(
    (set, get) => ({
      prevPathDeal: false,
      setPrevPathDeal: (value: boolean) => {
        set({ prevPathDeal: value });
      },
      currencies: [],
      user_rates: defaultUserRates,
      user_deposit_accounts: [],
      user_brokerage_accounts: [],
      user_accounts_show_config: defaultUserAccountsShowConfig,
      balanceCurrency: Currency.AMD,
      totalAmountBottomsheetSelected: "USD",
      setTotalAmountBottomsheetSelected: (v: string) =>
        set({ totalAmountBottomsheetSelected: v }),
      performanceAmountBottomsheetSelected: "USD",
      setPerformanceAmountBottomsheetSelected: (v: string) =>
        set({ performanceAmountBottomsheetSelected: v }),

      setBalanceCurrency: (v: string) => {
        set({ balanceCurrency: v });
      },

      loadCurrencies: async () => {
        const resp = await sircapApi.dashboard.currencies();

        if (resp.error) {
        } else {
          set({ currencies: resp.data.data });
        }
      },

      /**
       * Единая точка применения ответа `/dashboard/accounts`.
       *
       * Тот же payload приходит и HTTP-запросом, и push'ем по general-socket,
       * когда операция на другом устройстве изменила балансы. Пока обработка
       * была написана в двух местах, сокетный путь успел разойтись с HTTP:
       * он не фильтровал по саб-аккаунту, не пересчитывал show_config и
       * выбрасывал брокерские счета — то есть весь портфель.
       */
      applyUserAccounts: (raw: unknown) => {
        const { depositAccounts, brokerageAccounts } = normalizeAccountsPayload<
          IUserDepositAccount,
          IUserBrokerageAccount
        >(raw);

        const selectedSubAccount = getSubAccountId();
        const filteredDeposit = filterBySubAccount(
          depositAccounts,
          selectedSubAccount,
        );

        set({
          user_deposit_accounts: filteredDeposit,
          user_brokerage_accounts: filterBySubAccount(
            brokerageAccounts,
            selectedSubAccount,
          ),
          // Конфиг считается по НЕотфильтрованному списку — так было и раньше.
          user_accounts_show_config: mergeShowConfig(
            get().user_accounts_show_config,
            depositAccounts,
          ),
        });

        return filteredDeposit;
      },

      loadUserAccounts: async () => {
        try {
          const resp = await sircapApi.dashboard.accounts({
            timeout: 10 * 1000,
          });

          if (resp.error) return;

          const filteredDeposit = get().applyUserAccounts(resp.data.data);

          // Аналитика зачислений остаётся только на HTTP-пути: на сокетном она
          // засчитывала бы одно и то же зачисление заново при каждом push'е.
          fbTrackDepositCredits(filteredDeposit);
        } catch (error) {
          console.log(error);
        }
      },

      updateRates: (list) => {
        set({ user_rates: list });
      },

      updateAccountsShowConfig: (list) => {
        set({ user_accounts_show_config: list });
      },
    }),
    {
      name: "dashboard",
      version: 1,
      merge: (persistedState, currState) => {
        const {
          user_accounts_show_config,
          currencies,
          user_rates,
          balanceCurrency,
          totalAmountBottomsheetSelected,
          performanceAmountBottomsheetSelected,
        } = persistedState as Partial<DashboardState>;

        return {
          ...currState,
          user_rates: user_rates?.length ? user_rates : defaultUserRates,
          currencies: currencies || [],
          balanceCurrency: balanceCurrency || Currency.AMD,
          totalAmountBottomsheetSelected:
            totalAmountBottomsheetSelected || "USD",
          performanceAmountBottomsheetSelected:
            performanceAmountBottomsheetSelected || "USD",
          user_accounts_show_config: Object.keys(
            user_accounts_show_config || {},
          ).length
            ? user_accounts_show_config || {}
            : defaultUserAccountsShowConfig,
        };
      },
    },
  ),
);

export {
  getPositionAccrued,
  getPositionMarketPrice,
  type PositionForPricing,
  type RtPriceMap,
} from "./lib/position-price";

export const useCurrencies = () =>
  useStore(dashboardStore, (state) => state.currencies);

export const useRatesCurrencies = () =>
  useStore(dashboardStore, (state) =>
    state.currencies.filter((v) => Object.keys(Currency).includes(v.code)),
  );
